// v2 fish — a proper surface swimmer: a deep three-tone body (dark back, bright
// flank, pale belly), a snouted head with eye dots, a raked dorsal fin, paired
// pectoral fins swept back, and a narrow tail peduncle opening into a two-lobe
// crescent tail. Three liveries: silver-blue, teal and a koi-orange. Faces +X,
// swims at the classic waterline. Ported 1:1 from age-of-mcp's v2/fish.ts, using
// the library's shared `wildland` (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, wildland, type Part } from "../../lib";

const LIVERY = [
  { back: "#1f5f8a", flank: "#4f87ab", belly: "#d6e4ec", fin: "#16465f" },
  { back: "#1c6e5e", flank: "#4f9c86", belly: "#d9ead8", fin: "#114c40" },
  { back: "#c45a1e", flank: "#e08a48", belly: "#f2e0c8", fin: "#a8431a" },
];

function fishParts(v: number): Part[] {
  const c = LIVERY[v % LIVERY.length]!;
  const parts: Part[] = [
    // deep flank, dark back ridge, pale belly
    { geometry: new THREE.IcosahedronGeometry(0.025, 0), color: c.flank,
      position: [0.005, 0.027, 0], scale: [1.9, 1.0, 0.62] },
    { geometry: new THREE.IcosahedronGeometry(0.019, 0), color: c.back,
      position: [0, 0.038, 0], scale: [1.9, 0.7, 0.5] },
    { geometry: new THREE.IcosahedronGeometry(0.016, 0), color: c.belly,
      position: [0.008, 0.018, 0], scale: [1.7, 0.7, 0.55] },
    // snouted head
    { geometry: new THREE.IcosahedronGeometry(0.013, 0), color: c.flank,
      position: [0.05, 0.026, 0], scale: [1.1, 0.85, 0.62] },
    // raked dorsal fin
    { geometry: new THREE.ConeGeometry(0.01, 0.018, 4), color: c.fin,
      position: [0.0, 0.046, 0], rotation: [0, 0, 0.5], scale: [1, 1, 0.3] },
    // tail peduncle + crescent tail (upper and lower lobes)
    wildland.tube([-0.042, 0.027, 0], [-0.058, 0.027, 0], 0.008, 0.005, c.flank),
    { geometry: new THREE.ConeGeometry(0.0085, 0.03, 4), color: c.fin,
      position: [-0.066, 0.037, 0], rotation: [0, 0, 0.9], scale: [1, 1, 0.3] },
    { geometry: new THREE.ConeGeometry(0.0085, 0.03, 4), color: c.fin,
      position: [-0.066, 0.017, 0], rotation: [0, 0, 2.24], scale: [1, 1, 0.3] },
  ];
  // pectoral fins swept back and out
  for (const s of [1, -1] as const) {
    parts.push({ geometry: new THREE.ConeGeometry(0.006, 0.018, 3), color: c.fin,
      position: [0.028, 0.02, s * 0.017], rotation: [-s * 0.9, 0, 2.0],
      scale: [1, 1, 0.4] });
  }
  parts.push(...wildland.eyes(0.054, 0.031, 0.0085, 0.0035, "#10141a"));
  return parts;
}

export const fishV2 = defineModel({
  id: "fish-v2",
  batchId: "fable",
  name: "Fish (v2)",
  tags: ["creature","fish","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly fish.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.08,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => fishParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => fishParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => fishParts(2) },
  ],
  animations: [],
});
